import { Maybe } from '@graphql/generated';
import { getLocalStorage } from '@shared/core';

/**
 * Generates a localStorage key for auto-populated accommodation places count for a given event.
 * @param eventId - The event identifier.
 * @returns The localStorage key string.
 */
const AUTO_POPULATION_SUMMARY = (eventId: string) => `autoPopulatedAccommodationPlacesCount_${eventId}`;

/**
 * Retrieves the auto-populated accommodations count from localStorage for a given event.
 * @param eventId - The event identifier (optional).
 * @returns An object with newlyPopulatedCount and alreadyExistsCount. Defaults to { newlyPopulatedCount: 0, alreadyExistsCount: 0 } if not set or eventId is not provided.
 */
const get = (eventId?: Maybe<string>): { newlyPopulatedCount: number; alreadyExistsCount: number } => {
  if (!eventId) return { newlyPopulatedCount: 0, alreadyExistsCount: 0 };

  const localStorage = getLocalStorage();
  const counts = JSON.parse(localStorage.getItem(AUTO_POPULATION_SUMMARY(eventId)) || '[]') as [number, number];
  return { newlyPopulatedCount: counts[0] || 0, alreadyExistsCount: counts[1] || 0 };
};

/**
 * Sets the auto-populated accommodations count in localStorage for a given event.
 * @param eventId - The event identifier.
 * @param counts - Object containing newlyPopulatedCount and optional alreadyExistsCount.
 */
const set = (eventId: string, counts: { newlyPopulatedCount: number; alreadyExistsCount?: number }) => {
  const localStorage = getLocalStorage();
  localStorage.setItem(AUTO_POPULATION_SUMMARY(eventId), JSON.stringify([counts.newlyPopulatedCount, counts.alreadyExistsCount || 0]));
};

/**
 * Remove the auto-populated accommodations count from localStorage for a given event.
 * @param eventId - The event identifier.
 */
const remove = (eventId: string) => {
  const localStorage = getLocalStorage();
  localStorage.setItem(AUTO_POPULATION_SUMMARY(eventId), null);
};

export const autoPopulationSummary = { get, set, remove };
